import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";
import "./CongnitiveThinking.css";
import axios from "../axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilterCircleXmark } from "@fortawesome/free-solid-svg-icons";

function CognitiveThinkingPage({
  student,
  startDate,
  endDate,
  assessmentTypeId,
  subjectWiseData,
  subjectList,
}) {
  const [selectedSupplement, setSelectedSupplement] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [skillsData, setSkillsData] = useState([]);

  const skillOrder = [
    "Create",
    "Evaluate",
    "Analyze",
    "Apply",
    "Understand",
    "Remember",
    "Unspecified",
  ];

  const COLORS = [
    "#FF4D4D",
    "#FF9900",
    "#FFD700",
    "#66CC66",
    "#339933",
    "#3366FF",
    "#9933FF",
  ];

  useEffect(() => {
    if (!startDate || !endDate || !student?.id) return;
    const fetchOverallPercentage = async () => {
      try {
        const response = await axios.get(
          `/performance/standard/${student?.standard?.id}/section/${student?.section?.id}/student/${student?.id}/percentageWithSkills/get?subjectId=${selectedSubject}&startDate=${startDate}&endDate=${endDate}&assessmentEventId=${assessmentTypeId}`,
        );

        setSkillsData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchOverallPercentage();
  }, [startDate, endDate, student, selectedSubject, assessmentTypeId]);

  const subjectsPerformance =
    subjectWiseData?.map((subject) => ({
      id: subject?.result?.[0]._id,
      name: subject?.result?.[0]?.name,
      score: Math.round(subject?.averagePercentageScore || 0),
    })) || [];

  const skills = skillsData?.data?.map((skill) => ({
    name: skill.skillDetails[0].name,
    score: Math.round(skill?.percentageMarks),
  }));

  const sortedSkills = Array.isArray(skills)
    ? skillOrder.map((skillName) => {
        const existingSkill = skills.find((skill) => skill.name === skillName);
        return {
          ...existingSkill,
          name: skillName === "None" ? "Unspecified" : skillName,
          score: existingSkill?.score ?? 0, // Default to 0 if skill is missing
        };
      })
    : [];

  const overallScore =
    skillsData && skillsData?.overallCognitiveSkillPercentage;

  const data = [
    { name: "Score", value: overallScore },
    { name: "Remaining", value: Math.max(100 - overallScore, 0) },
  ];

  return (
    <div className="card-cognitive mt-2">
      <div className="card-content-cognitive ">
        <div className="flex items-center justify-between mb-2 p-3 h-16 bg-gray-200">
          <h2 className="card-title">Cognitive Thinking Skills Levels</h2>
          <div className="filter-group-cognitive">
            <select
              value={selectedSupplement}
              onChange={(e) => setSelectedSupplement(e.target.value)}
              className="form-select"
            >
              <option value="">Supplements</option>
              <option value="assessment">Assessment</option>
            </select>

            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="form-select"
            >
              <option value="">Subject Name</option>
              {subjectList?.map((subject, index) => (
                <option value={subject.id} key={subject.id}>
                  {subject?.name}
                </option>
              ))}
            </select>

            <button
              className="clear-filter-btn"
              onClick={() => {
                setSelectedSupplement("");
                setSelectedSubject("");
              }}
            >
              <FontAwesomeIcon icon={faFilterCircleXmark} />
            </button>
          </div>
        </div>

        <hr className="separator" />

        <div className="grid-container-cognitive p-4">
          {/* Image */}
          <div className="image-container">
            <img
              src="/images/coginitive1.png"
              alt="Bloom's Taxonomy"
              className="taxonomy-image"
            />
          </div>

          {/* Skills */}
          <div className="skills-container">
            {sortedSkills?.map((skill) => (
              <div key={skill.name} className="skill-row">
                <span className="skill-name">{skill.name}</span>
                <div className="progress-bar-skills">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${skill.score ?? 0}%` }}
                  ></div>
                </div>

                <span className="skill-score">{skill.score ?? 0}%</span>
              </div>
            ))}
          </div>

          {/* Score Chart */}
          <div className="score-chart">
            <h2 className="performance-title">Overall Performance</h2>
            <ResponsiveContainer width={250} height={250}>
              <PieChart>
                <Pie
                  data={data.length ? data : [{ name: "No data", value: 100 }]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  startAngle={230}
                  endAngle={-50}
                  dataKey="value"
                >
                  {(data.length ? data : [{ name: "No data", value: 100 }]).map(
                    (entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === 0 ? COLORS[4] : "#ccc"}
                      />
                    ),
                  )}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="score-text">{overallScore}%</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CognitiveThinkingPage;
